/*
 * Author: zoesvision.com
 * Purpose: Judge.me review loading, normalization, rendering, and isolated Embla lifecycle.
 */

if (!customElements.get('z-judgeme-carousel')) {
  customElements.define('z-judgeme-carousel', class ZJudgeMeCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.viewport = this.querySelector('[data-z-judgeme-viewport]');
      this.track = this.querySelector('[data-z-judgeme-track]');
      this.previousButton = this.querySelector('[data-z-previous]');
      this.nextButton = this.querySelector('[data-z-next]');
      this.diagnostic = this.querySelector('[data-z-diagnostic]');
      this.liveRegion = this.querySelector('[data-z-live-region]');
      this.productCache = new Map();
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.bindEvents();
      this.resizeObserver = new ResizeObserver(() => this.scheduleCardMeasurement());
      this.resizeObserver.observe(this);
      this.load();
    }

    disconnectedCallback() {
      this.destroy();
    }

    bindEvents() {
      const signal = this.abortController.signal;
      this.addEventListener('keydown', this.handleKeydown, { signal });
      this.previousButton?.addEventListener('click', () => this.navigate('previous'), { signal });
      this.nextButton?.addEventListener('click', () => this.navigate('next'), { signal });
      this.reducedMotion.addEventListener('change', this.handleMotionChange, { signal });
    }

    async load() {
      if (!this.viewport || !this.track) return;

      if (!this.dataset.zPublicToken) {
        this.handleUnavailable(this.dataset.zMessageMissingToken);
        return;
      }

      try {
        const reviews = await this.fetchReviews();
        const productReviews = reviews.filter((review) => review.productUrl);
        const resolvedReviews = (await Promise.all(
          productReviews.map((review) => this.resolveProduct(review))
        )).filter(Boolean);

        if (!resolvedReviews.length) {
          this.handleUnavailable(this.dataset.zMessageNoReviews);
          return;
        }

        this.renderReviews(resolvedReviews);
        this.initializeEmbla();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.handleUnavailable(this.dataset.zMessageRequestFailed);
      }
    }

    async fetchReviews() {
      if (this.dataset.zSource === 'featured') {
        return this.fetchWidget('featured_carousel');
      }

      if (this.dataset.zRating === '4_5') {
        const responses = await Promise.all([
          this.fetchWidget('reviews_tab', '4'),
          this.fetchWidget('reviews_tab', '5')
        ]);
        const unique = new Map();
        responses.flat().forEach((review) => unique.set(review.id, review));
        return Array.from(unique.values())
          .sort((a, b) => b.createdAt - a.createdAt)
          .slice(0, this.reviewLimit);
      }

      const rating = this.dataset.zRating === 'all' ? '' : this.dataset.zRating;
      return this.fetchWidget('reviews_tab', rating);
    }

    async fetchWidget(endpoint, rating = '') {
      const url = new URL(`https://api.judge.me/api/v1/widgets/${endpoint}`);
      url.searchParams.set('shop_domain', this.dataset.zShopDomain);
      url.searchParams.set('api_token', this.dataset.zPublicToken);
      url.searchParams.set('json_request', 'true');
      url.searchParams.set('per_page', String(this.reviewLimit));
      if (endpoint === 'reviews_tab') {
        url.searchParams.set('page', '1');
        if (rating) url.searchParams.set('filter_rating', rating);
      }

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Judge.me request failed: ${response.status}`);

      const payload = await response.json();
      const html = payload[endpoint];
      if (typeof html !== 'string') throw new Error('Judge.me response did not contain widget HTML');

      return this.parseWidgetHtml(endpoint, html).slice(0, this.reviewLimit);
    }

    parseWidgetHtml(endpoint, html) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const selector = endpoint === 'featured_carousel'
        ? '.jdgm-carousel-item'
        : '.jdgm-rev';

      return Array.from(documentFragment.querySelectorAll(selector))
        .map((item) => (
          endpoint === 'featured_carousel'
            ? this.normalizeFeatured(item)
            : this.normalizeLatest(item)
        ))
        .filter((review) => review.id && review.body && review.rating > 0);
    }

    normalizeFeatured(item) {
      const timestamp = item.querySelector('.jdgm-carousel-item__timestamp')?.dataset.time || '';
      const productLink = item.querySelector('.jdgm-carousel-item__product');
      return {
        id: item.dataset.reviewId || '',
        reviewerName: this.text(item, '.jdgm-carousel-item__reviewer-name'),
        rating: item.querySelectorAll('.jdgm-carousel-item__review-rating .jdgm--on').length,
        body: this.text(item, '.jdgm-carousel-item__review-body'),
        productTitle: this.text(item, '.jdgm-carousel-item__product-title'),
        productUrl: this.absoluteUrl(productLink?.getAttribute('href')),
        productImageUrl: '',
        createdAt: this.parseDate(timestamp)
      };
    }

    normalizeLatest(item) {
      const ratingElement = item.querySelector('.jdgm-rev__rating');
      const timestamp = item.querySelector('.jdgm-rev__timestamp')?.dataset.content || '';
      const productLink = item.querySelector('.jdgm-rev__prod-link');
      return {
        id: item.dataset.reviewId || '',
        reviewerName: this.text(item, '.jdgm-rev__author'),
        rating: Number(ratingElement?.dataset.score)
          || item.querySelectorAll('.jdgm-rev__rating .jdgm--on').length,
        body: this.text(item, '.jdgm-rev__body'),
        productTitle: productLink?.textContent?.trim() || '',
        productUrl: this.absoluteUrl(productLink?.getAttribute('href')),
        productImageUrl: '',
        createdAt: this.parseDate(timestamp)
      };
    }

    text(root, selector) {
      return root.querySelector(selector)?.textContent?.replace(/\s+/g, ' ').trim() || '';
    }

    absoluteUrl(value) {
      if (!value) return '';
      try {
        return new URL(value, location.origin).href;
      } catch {
        return '';
      }
    }

    parseDate(value) {
      const milliseconds = Date.parse(value);
      if (!Number.isNaN(milliseconds)) return milliseconds;
      const seconds = Number(value);
      return Number.isFinite(seconds) ? seconds * 1000 : 0;
    }

    async resolveProduct(review) {
      const handle = new URL(review.productUrl).pathname.match(/^\/products\/([^/]+)/)?.[1];
      if (!handle) return null;

      if (!this.productCache.has(handle)) {
        this.productCache.set(handle, fetch(`/products/${handle}.js`, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' }
        }).then((response) => response.ok ? response.json() : null));
      }

      const product = await this.productCache.get(handle);
      if (!product) return null;

      return {
        ...review,
        productTitle: review.productTitle || product.title || '',
        productImageUrl: product.featured_image || ''
      };
    }

    renderReviews(reviews) {
      const fragment = document.createDocumentFragment();
      reviews.forEach((review) => fragment.append(this.createSlide(review)));
      this.track.replaceChildren(fragment);
      // zoesvision.com | 2026-08-16 | Purpose: Observe review cards inserted by the isolated API renderer.
      this.querySelector('z-animate-on-scroll')?.refresh();
      this.items = Array.from(this.track.querySelectorAll('[data-z-judgeme-slide]'));
      this.hideDiagnostic();
      this.scheduleCardMeasurement();
    }

    createSlide(review) {
      const slide = document.createElement('div');
      slide.className = 'z-judgeme-carousel__slide';
      slide.dataset.zJudgemeSlide = '';

      const card = document.createElement('article');
      card.className = 'z-judgeme-card';
      card.dataset.zReviewId = review.id;
      // zoesvision.com | 2026-08-17 | Purpose: Animate the visual review card without overriding Embla's loop positioning transform on the slide root.
      card.dataset.zScrollItem = '';

      const reviewer = document.createElement('p');
      reviewer.className = 'z-judgeme-card__reviewer';
      reviewer.textContent = review.reviewerName;

      const rating = document.createElement('div');
      rating.className = 'z-judgeme-card__rating';
      rating.setAttribute('role', 'img');
      rating.setAttribute(
        'aria-label',
        (this.dataset.zLabelRating || `${review.rating} out of 5 stars`)
          .replace('[rating]', String(review.rating))
      );
      rating.append(this.createStars(review.rating));

      const body = document.createElement('p');
      body.className = 'z-judgeme-card__body';
      body.textContent = review.body;

      const product = document.createElement('a');
      product.className = 'z-judgeme-card__product';
      product.href = review.productUrl.split('#')[0];
      if (review.productImageUrl) {
        const imageWrapper = document.createElement('span');
        imageWrapper.className = 'z-judgeme-card__image-wrapper';
        const image = document.createElement('img');
        image.className = 'z-judgeme-card__image';
        image.src = review.productImageUrl;
        image.alt = review.productTitle;
        image.loading = 'lazy';
        image.width = 160;
        image.height = 160;
        imageWrapper.append(image);
        product.append(imageWrapper);
      }
      const productTitle = document.createElement('span');
      productTitle.className = 'z-judgeme-card__product-title';
      productTitle.textContent = review.productTitle;
      product.append(productTitle);

      card.append(reviewer, rating, body, product);
      slide.append(card);
      return slide;
    }

    createStars(rating) {
      const stars = document.createElement('span');
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = '★'.repeat(Math.round(rating)).padEnd(5, '☆');
      return stars;
    }

    scheduleCardMeasurement() {
      cancelAnimationFrame(this.measurementFrame);
      this.measurementFrame = requestAnimationFrame(() => this.measureCards());
    }

    measureCards() {
      const cards = Array.from(this.querySelectorAll('.z-judgeme-card'));

      cards.forEach((card) => {
        const body = card.querySelector('.z-judgeme-card__body');
        body?.style.setProperty('--z-judgeme-auto-lines', '1');
      });

      cards.forEach((card) => {
        const body = card.querySelector('.z-judgeme-card__body');
        const product = card.querySelector('.z-judgeme-card__product');
        if (!body || !product) return;

        const bodyStyle = getComputedStyle(body);
        const lineHeight = parseFloat(bodyStyle.lineHeight);
        const reservedGap = parseFloat(bodyStyle.marginBottom) || 0;
        if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

        const availableHeight = product.getBoundingClientRect().top
          - body.getBoundingClientRect().top
          - reservedGap;
        const lines = Math.max(1, Math.floor(availableHeight / lineHeight));

        body.style.setProperty('--z-judgeme-auto-lines', String(lines));
      });
    }

    initializeEmbla() {
      this.items ||= Array.from(this.track.querySelectorAll('[data-z-judgeme-slide]'));
      if (!this.items.length) return;

      if (typeof globalThis.EmblaCarousel !== 'function') {
        this.dependencyAttempts = (this.dependencyAttempts || 0) + 1;
        if (this.dependencyAttempts <= 120 && this.isConnected) {
          this.dependencyFrame = requestAnimationFrame(() => this.initializeEmbla());
        } else {
          this.dataset.zReady = 'true';
        }
        return;
      }

      this.embla = globalThis.EmblaCarousel(this.viewport, {
        loop: this.dataset.zLoop === 'true',
        align: 'start',
        containScroll: 'trimSnaps',
        slidesToScroll: 1,
        duration: this.reducedMotion.matches ? 0 : 25,
        inViewThreshold: 0.35,
        watchDrag: this.items.length > 1
      });
      this.handleSelect = this.syncState.bind(this);
      this.embla.on('select', this.handleSelect);
      this.embla.on('slidesInView', this.handleSelect);
      this.embla.on('reInit', this.handleSelect);
      this.syncState();
      this.dataset.zReady = 'true';
    }

    navigate(direction) {
      if (!this.embla) return;
      direction === 'previous' ? this.embla.scrollPrev() : this.embla.scrollNext();
    }

    handleKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.matches('input, select, textarea')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.navigate(event.key === 'ArrowLeft' ? 'previous' : 'next');
    }

    handleMotionChange() {
      this.embla?.reInit({ duration: this.reducedMotion.matches ? 0 : 25 });
    }

    syncState() {
      if (!this.embla) return;

      if (this.previousButton) this.previousButton.disabled = !this.embla.canScrollPrev();
      if (this.nextButton) this.nextButton.disabled = !this.embla.canScrollNext();

      const visible = new Set(this.embla.slidesInView());
      this.items.forEach((item, index) => {
        const link = item.querySelector('a');
        const isVisible = visible.has(index);
        item.setAttribute('aria-hidden', String(!isVisible));
        if (link) link.tabIndex = isVisible ? 0 : -1;
      });

      if (this.liveRegion) {
        this.liveRegion.textContent = this.formatLabel(
          this.dataset.zLabelStatus,
          this.embla.selectedScrollSnap() + 1,
          this.items.length
        );
      }

      this.scheduleCardMeasurement();
    }

    handleUnavailable(message) {
      if (this.dataset.zDesignMode === 'true') {
        this.showDiagnostic(message);
        if (this.dataset.zHasSamples === 'true') {
          this.items = Array.from(this.track.querySelectorAll('[data-z-judgeme-slide]'));
          this.initializeEmbla();
        }
        return;
      }
      this.closest('.z-judgeme-carousel__width')?.setAttribute('hidden', '');
    }

    showDiagnostic(message) {
      if (!this.diagnostic) return;
      this.diagnostic.textContent = message || '';
      this.diagnostic.hidden = false;
    }

    hideDiagnostic() {
      if (!this.diagnostic) return;
      this.diagnostic.textContent = '';
      this.diagnostic.hidden = true;
    }

    formatLabel(template, index, total) {
      return (template || `${index} / ${total}`)
        .replace('[index]', String(index))
        .replace('[total]', String(total));
    }

    get reviewLimit() {
      return Math.min(30, Math.max(1, Number(this.dataset.zReviewCount) || 8));
    }

    destroy() {
      cancelAnimationFrame(this.dependencyFrame);
      cancelAnimationFrame(this.measurementFrame);
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.embla?.destroy();
      this.embla = null;
      this.productCache?.clear();
      this.isInitialized = false;
      this.dataset.zReady = 'false';
    }
  });
}
